import {skipToken} from '@reduxjs/toolkit/query';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useGetCurrentUserInfoQuery, useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {useGetServiceOrderQuery, useGetServiceOrderVariantQuery} from '@dms/api/workshop';

import {Nullish} from 'shared/web';

interface UseMoveMechanicProps {
  serviceCaseId: string;
  targetServiceOrderId: string | Nullish;
  isSkipped: boolean;
}

export function useMoveMechanic(props: UseMoveMechanicProps) {
  // undefined = untouched, so the derived default (logged-in mechanic) applies
  const [selectedMechanicId, setSelectedMechanicId] = useState<string | Nullish>(undefined);

  const hasTargetOrder = isNotNil(props.targetServiceOrderId);

  const {data: targetServiceOrder} = useGetServiceOrderQuery(
    props.isSkipped || isNil(props.targetServiceOrderId)
      ? skipToken
      : {serviceCaseId: props.serviceCaseId, serviceOrderId: props.targetServiceOrderId}
  );
  const targetServiceOrderVariantId = hasTargetOrder
    ? targetServiceOrder?.serviceOrderVariantId
    : undefined;
  const {data: targetServiceOrderVariant} = useGetServiceOrderVariantQuery(
    isNotNil(targetServiceOrderVariantId)
      ? {serviceOrderVariantId: targetServiceOrderVariantId}
      : skipToken
  );
  const targetAuthorizationProfileId = targetServiceOrderVariant?.authorizationProfileId;

  // Until a target order is picked, every mechanic is offered; after that, only the mechanics
  // of the target order's authorization profile (BE rejects others with SVC-FE-ER-0241).
  const {currentData: mechanics, isFetching: areMechanicsLoading} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: hasTargetOrder ? targetAuthorizationProfileId : undefined,
      isActive: true,
    },
    {skip: props.isSkipped || (hasTargetOrder && isNil(targetAuthorizationProfileId))}
  );
  const {data: currentUser} = useGetCurrentUserInfoQuery(undefined, {skip: props.isSkipped});

  const defaultMechanicId = mechanics?.find(
    (mechanic) => mechanic.employeeId === currentUser?.id
  )?.employeeId;
  const chosenMechanicId =
    selectedMechanicId === undefined ? (defaultMechanicId ?? null) : selectedMechanicId;
  // A mechanic missing from the target order's list is dropped; while the list loads, keep the choice.
  const isChosenMechanicAvailable =
    isNil(mechanics) || mechanics.some((mechanic) => mechanic.employeeId === chosenMechanicId);

  return {
    mechanicId: isChosenMechanicAvailable ? chosenMechanicId : null,
    mechanicOptions: (mechanics ?? []).map((mechanic) => ({
      label: mechanic.name,
      value: mechanic.employeeId,
    })),
    areMechanicsLoading,
    setMechanicId: setSelectedMechanicId,
  };
}
